import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OnlineReferralModal from '../home/components/OnlineReferralModal';
import PageNavbar from '../../components/feature/PageNavbar';

export default function TreatmentPage() {
  const navigate = useNavigate();
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);

  const streams = [
    {
      title: 'VIP - Veteran Intensive Programs',
      description: 'VIP is a 2–4 week intensive day or residential recovery program developed in conjunction with Veterans, for Veterans.',
      features: [
        'Coordinated medical, psychological and physical rehabilitation',
        'Restore function, confidence and direction',
        'Specialized support for service-related trauma and complex health challenges'
      ],
      featureColor: 'bg-teal-500',
      link: '/treatment/vip',
      isExternal: false,
      image: 'https://static.readdy.ai/image/c1d3a3065b1a0920ec31881e87c350dd/99fdbcce43f3ad70e3eaae2defe8ed06.jpeg'
    },
    {
      title: 'PATH Programs',
      description: 'Preferred Alternatives To Hospitalisation - A 2-4 week intensive residential neuroplasticity-based program designed for people living with neurological disabilities and complex life challenges.',
      features: [
        'Intensive Neuroplasticity-Based Capacity Building',
        'Robotics, AI & Virtual Reality Rehabilitation',
        'Lifestyle Medicine & Multidisciplinary Therapies'
      ],
      featureColor: 'bg-blue-500',
      link: '/treatment/path',
      isExternal: false,
      image: 'https://public.readdy.ai/ai/img_res/b68052da4ef38ead2e7850bca791ae9a.jpg'
    },
    {
      title: 'START Programs',
      description: 'Short Term Acute Rehabilitation for Trauma - A 2-4 week intensive neuroplasticity-focused treatment program for individuals experiencing trauma, PTSD and Traumatic Brain Injury.',
      features: [
        'PTSD and Trauma Recovery',
        'Comprehensive TBI Assessment & Treatment',
        'Advanced Diagnostics & Rehabilitation'
      ],
      featureColor: 'bg-purple-500',
      link: '/treatment/start',
      isExternal: false,
      image: 'https://readdy.ai/api/search-image?query=A%20compassionate%20and%20professional%20trauma%20rehabilitation%20center%20showing%20a%20calm%20therapeutic%20environment%20with%20a%20patient%20receiving%20PTSD%20treatment%2C%20advanced%20brain%20imaging%20equipment%2C%20neuropsychological%20assessment%20tools%2C%20peaceful%20therapy%20rooms%20with%20soft%20lighting%2C%20medical%20professionals%20conducting%20cognitive%20rehabilitation%2C%20modern%20clinical%20setting%20with%20warm%20colors%2C%20sense%20of%20safety%20and%20recovery%2C%20photorealistic%20medical%20facility%20photography&width=800&height=600&seq=start001&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <PageNavbar />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Section Title */}
        <div className="mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Specialized Treatment Streams</h2>
          <p className="text-gray-600 max-w-3xl">
            Our intensive treatment programs are designed around distinct streams, each tailored to specific rehabilitation needs and recovery goals. Each stream combines cutting-edge technology with evidence-based practices for optimal outcomes.
          </p>
        </div>

        {/* Treatment Streams Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {streams.map((stream, index) => (
            <div
              key={index}
              className="bg-gray-50 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300"
            >
              {/* Card Header with Image */}
              <div className="relative h-48 w-full overflow-hidden">
                <img 
                  src={stream.image}
                  alt={stream.title}
                  className="w-full h-full object-cover object-center"
                />
              </div>

              {/* Card Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{stream.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-5">{stream.description}</p>
                
                {/* Features List */}
                <ul className="space-y-2.5 mb-6">
                  {stream.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-3 text-sm text-gray-700">
                      <span className={`w-2 h-2 ${stream.featureColor} rounded-full flex-shrink-0`}></span>
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Link */}
                <a
                  href={stream.link}
                  onClick={(e) => {
                    if (stream.link !== '#' && !stream.isExternal) {
                      e.preventDefault();
                      navigate(stream.link);
                    }
                  }}
                  target={stream.isExternal ? '_blank' : undefined}
                  rel={stream.isExternal ? 'noopener noreferrer' : undefined}
                  className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 text-sm font-medium transition-colors cursor-pointer"
                >
                  Click for more details
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="bg-gray-50 rounded-2xl p-10 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-3">Ready to Begin Your Recovery Journey?</h3>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Our team will help determine which treatment stream is most appropriate for your specific needs and goals.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <button
              onClick={() => setIsReferralModalOpen(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap font-medium"
            >
              <i className="ri-file-list-3-line"></i>
              Online Referral
            </button>
            <a
              href="mailto:enquire@archealth.net.au"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap font-medium"
            >
              <i className="ri-mail-line"></i>
              Contact Us
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-100 mt-8">
        <div className="max-w-7xl mx-auto px-6 py-8 text-center">
          <p className="text-gray-500 text-sm">© 2025 Accelerated Recovery Centre (ARC). All rights reserved.</p>
        </div>
      </div>

      {/* Online Referral Modal */}
      <OnlineReferralModal
        isOpen={isReferralModalOpen}
        onClose={() => setIsReferralModalOpen(false)}
      />
    </div>
  );
}
